import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ll-livvia-bg relative overflow-hidden rounded-2xl p-4 sm:p-5 shadow-[0_18px_40px_-20px_rgba(13,148,136,0.45)]"
}
const _hoisted_2 = { class: "relative flex gap-4" }
const _hoisted_3 = { class: "flex size-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-fuchsia-600 shadow-md ring-[3px] ring-white/25" }
const _hoisted_4 = { class: "min-w-0 flex-1" }
const _hoisted_5 = { class: "text-[11px] font-bold uppercase tracking-wider text-purple-200" }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 0,
  class: "mt-2 text-xs text-white/70"
}
const _hoisted_8 = {
  key: 1,
  class: "mt-3 flex flex-wrap gap-2"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  key: 0,
  class: "inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800"
}

import { computed, h, VNode } from 'vue';
import { useI18n } from 'vue-i18n';
import { SparklesIcon, CheckIcon } from '@heroicons/vue/24/solid';
import useLivviaStore from '@/store/livvia';
import LivviaProcessing from '@/components/tailwind/livvia/LivviaProcessing.vue';

interface LivviaAction {
  key: string;
  label: string;
  variant: 'primary' | 'instagram' | 'facebook' | 'ghost';
  icon?: VNode | null;
  onClick: () => void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LivviaStrip',
  setup(__props) {

const { t } = useI18n();
const livvia = useLivviaStore();

// Brand-mark inline SVGs (Instagram / Facebook are not in heroicons).
// Rendered via component :is="a.icon"
const IgIcon = () => h('svg', {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
}, [
  h('rect', {
    x: 2,
    y: 2,
    width: 20,
    height: 20,
    rx: 6,
  }),
  h('circle', {
    cx: 12,
    cy: 12,
    r: 4,
  }),
  h('circle', {
    cx: 17.5,
    cy: 6.5,
    r: 1.3,
    fill: 'currentColor',
    stroke: 'none',
  }),
]);
// Retained on purpose while the Facebook CTA is hidden below — deleting it
// would mean redrawing the mark when Facebook ships.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FbIcon = () => h('svg', {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
}, [
  h('path', { d: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z' }),
]);

const CheckIconV = () => h(CheckIcon, { class: 'size-4' });
const SparkleIconV = () => h(SparklesIcon, { class: 'size-4' });

const message = computed(() => {
  if (livvia.state === 'arrival') return t('livvia.arrival.message');
  if (livvia.state === 'prepare') return t('livvia.prepare.message');
  if (livvia.state === 'prepare_success') return t('livvia.prepare.success');
  if (livvia.state === 'post_live') {
    return t('livvia.postLive.message', {
      duration: livvia.lastLive?.durationLabel || '',
      comments: livvia.lastLive?.commentsCount ?? 0,
    });
  }
  if (livvia.state === 'post_live_deferred') return t('livvia.postLive.deferred');
  if (livvia.state === 'video_picker') return t('livvia.videoPicker.message');
  if (livvia.state === 'processing') return t('livvia.processing.message');
  if (livvia.state === 'result') {
    if (livvia.allChargesSent) {
      return t('livvia.result.sentMessage', {
        count: livvia.resultData?.clientCount ?? 0,
      });
    }
    return t('livvia.result.message', {
      count: livvia.resultData?.clientCount ?? 0,
    });
  }
  return '';
});
const note = computed(() => {
  if (livvia.state === 'arrival') return t('livvia.arrival.note');
  if (livvia.state === 'prepare') return t('livvia.prepare.note');
  return '';
});
const actions = computed<LivviaAction[]>(() => {
  if (livvia.state === 'arrival') {
    return [
      {
        key: 'ig', label: t('livvia.arrival.actionInstagram'), variant: 'instagram', icon: IgIcon(), onClick: () => livvia.openConnect('instagram'),
      },
      // Facebook is hidden for now — the FAQ tells sellers "Instagram hoje,
      // Facebook chegando em breve", and the New Integration drawer already
      // shows it as "em breve" rather than connectable. Offering it as a
      // primary CTA on the very first screen contradicts both. Restore by
      // uncommenting; the handler and copy key are still in place.
      // {
      //   key: 'fb', label: t('livvia.arrival.actionFacebook'), variant: 'facebook',
      //   icon: FbIcon(), onClick: () => livvia.openConnect('facebook'),
      // },
    ];
  }
  if (livvia.state === 'prepare') {
    return [
      {
        key: 'activate', label: t('livvia.prepare.actionActivate'), variant: 'primary', icon: CheckIconV(), onClick: livvia.activatePrepare,
      },
      {
        key: 'skip', label: t('livvia.prepare.actionSkip'), variant: 'ghost', onClick: livvia.skipPrepare,
      },
    ];
  }
  if (livvia.state === 'post_live') {
    return [
      {
        key: 'build', label: t('livvia.postLive.actionBuild'), variant: 'primary', icon: SparkleIconV(), onClick: livvia.startBuildingCarts,
      },
      {
        key: 'defer', label: t('livvia.postLive.actionDefer'), variant: 'ghost', onClick: livvia.deferPostLive,
      },
    ];
  }
  if (livvia.state === 'post_live_deferred') {
    return [
      {
        key: 'build', label: t('livvia.postLive.actionBuildNow'), variant: 'primary', icon: SparkleIconV(), onClick: livvia.startBuildingCarts,
      },
    ];
  }
  return [];
});

const btnClass = (variant: LivviaAction['variant']) => {
  const base = 'inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2';
  switch (variant) {
    case 'instagram':
      return `${base} bg-gradient-to-br from-amber-300 via-pink-600 to-purple-700 text-white hover:brightness-105 focus-visible:outline-pink-600`;
    case 'facebook':
      return `${base} bg-[#1877f2] text-white hover:bg-[#1666d1] focus-visible:outline-[#1877f2]`;
    case 'ghost':
      return `${base} border border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:text-gray-900 focus-visible:outline-gray-500`;
    case 'primary':
    default:
      // Match the AI accent — Livvia's primary CTAs are AI actions
      // (montar carrinhos, ativar cadastro, etc.), so they live in the
      // purple→fuchsia family, not LiveLink teal.
      return `${base} bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white hover:from-purple-700 hover:to-fuchsia-700 focus-visible:outline-purple-600`;
  }
};

return (_ctx: any,_cache: any) => {
  return (_unref(livvia).isVisible)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _cache[0] || (_cache[0] = _createElementVNode("div", {
          "aria-hidden": "true",
          class: "pointer-events-none absolute -right-14 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl"
        }, null, -1)),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, [
            _createVNode(_unref(SparklesIcon), { class: "size-5 text-white" })
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)('livvia.who')), 1),
            _createElementVNode("p", {
              class: "mt-1 max-w-2xl text-[15px] leading-relaxed text-white/90 sm:text-base [&_b]:text-white [&_b]:font-semibold",
              innerHTML: message.value
            }, null, 8, _hoisted_6),
            (note.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(note.value), 1))
              : _createCommentVNode("", true),
            (actions.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(actions.value, (a) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: a.key,
                      type: "button",
                      class: _normalizeClass(btnClass(a.variant)),
                      onClick: a.onClick
                    }, [
                      (a.icon)
                        ? (_openBlock(), _createBlock(_resolveDynamicComponent(a.icon), {
                            key: 0,
                            class: "size-4",
                            "aria-hidden": "true"
                          }))
                        : _createCommentVNode("", true),
                      _createTextVNode(" " + _toDisplayString(a.label), 1)
                    ], 10, _hoisted_9))
                  }), 128)),
                  (_unref(livvia).state === 'arrival')
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, " 🎁 " + _toDisplayString(_unref(t)('livvia.arrival.gift')), 1))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            (_unref(livvia).state === 'processing')
              ? (_openBlock(), _createBlock(LivviaProcessing, { key: 2 }))
              : _createCommentVNode("", true)
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})